import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSSRProduct } from '../../contexts/pdp/useSSRProduct';
import { isServer } from '../../utils/utils';
import { useBffGet } from '../bff';
import { useSSRPreviewMode } from '../customers/useSSRPreviewMode';
import { BffQueryKey } from '../model/BffQueryKey';
import type { BFFHttpError } from '../model/BFFHttpError';
import type { BFFQueryOptions } from '../model/BFFQueryOptions';
import type { Product } from './model';

export const useProduct = (
    productId?: string,
    options?: BFFQueryOptions<Product>,
): UseQueryResult<Product, BFFHttpError> => {
    const isSSRPreviewMode = useSSRPreviewMode();
    const { product: serverProduct } = useSSRProduct();
    const enabled = useMemo(
        () =>
            (!isSSRPreviewMode || !isServer) &&
            (typeof options?.enabled !== 'undefined' ? !!options.enabled : !!productId),
        [isSSRPreviewMode, options, productId],
    );
    return useBffGet<Product>(
        productId ? `/products/${productId}` : null,
        [],
        [BffQueryKey.PRODUCT, productId ?? ''],
        {
            enabled,
            initialData:
                !isSSRPreviewMode && productId === serverProduct?.id ? serverProduct : undefined, // initial data for non-preview mode
            keepPreviousData: !isSSRPreviewMode, // keep previous data for non-preview mode
            retryOnMount: isSSRPreviewMode, // retry on mount for preview mode
            headers: {
                'Cache-Control': isSSRPreviewMode ? 'no-cache' : 'no-store',
            },
        },
    );
};
